import React from 'react';
import { Button } from '../ui/Button/Button';

function isChunkLoadError(error: Error | null): boolean {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isChunkError = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-svh flex flex-col items-center justify-center p-6 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600 mb-6 text-center max-w-md">
            {isChunkError
              ? "The page couldn't load. This sometimes happens after we update the app—a quick refresh usually fixes it."
              : this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <Button
            variant="default"
            onClick={() => window.location.reload()}
          >
            Refresh page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
} 